import * as React from 'react';
import { FC, useMemo, useState } from 'react';
import { Form, Alert, Nav } from 'reactstrap';
import { Tab } from '@material-ui/core';
import AppTabs from '../../../components/AppTabs';
import styled from 'styled-components';
import { Row } from '../../../components/Forms/table/Row';
import { Col } from '../../../components/Forms/table/Column';
import { SettingsItemContentProps } from '../SettingsItem';
import {
    InvoiceSettingsData,
    InvoicesProvider,
    FeatureEnum,
    CompanyLegalInfo,
    IntegrationAppointmintInvoices,
} from '../../../models';
import { useTranslations, getLanguage } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import Typography from '../../../components/Typography';
import { TextInputField, ComboField, SwitchField, AutocompleteTextField, PlaceholderTextField } from '../../../components/Forms/fields';
import { CheckInputField } from '../../../components/Forms/fields/CheckInputField';
import { useSelector } from 'react-redux';
import { getFeatures } from '../../../store/session/selectors';
import { useTaxData } from '../../../hooks/useTaxData';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { DEFAULT_APPOINTMENT_PACK_DETAILS_TEMPLATE } from '../../../shared/appointmint/invoice';
import TabButton from '../../../components/TabButton';
import AppButton from '../../../components/AppButton';
import moment from 'moment';

type DivProps = { children?: React.ReactNode; style?: React.CSSProperties };

const SectionRow = styled.div<DivProps>`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

const AtSectionHeader = styled.div<DivProps>`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
    margin-bottom: var(--spacer-S);
`;

const SaveRow = styled.div<DivProps>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: var(--spacer-M);
`;

const SaveRowRight = styled.div<DivProps>`
    display: flex;
    justify-content: flex-end;
    margin-top: var(--spacer-M);
`;

const AtLogoCol = styled.div<DivProps>`
    text-align: center;
    img {
        height: 180px;
        max-width: 100%;
        display: block;
        margin: 0 auto;
    }
`;

const StatusBadge = styled.span<{ $configured: boolean; children?: React.ReactNode }>`
    display: inline-block;
    padding: var(--spacer-XXS) var(--spacer-XS);
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    background-color: ${p => (p.$configured ? 'var(--success-bg)' : 'var(--bg-light)')};
    color: ${p => (p.$configured ? 'var(--success-color)' : 'var(--text-muted)')};
    border: var(--border-thin) solid ${p => (p.$configured ? 'var(--success-border)' : 'var(--appLigthGrey)')};
`;

export interface InvoicesSettingsProps extends SettingsItemContentProps {
    values: InvoiceSettingsData,
    onChange: (settings: InvoiceSettingsData, save?: boolean) => void,
}

const InvoicesSettings: FC<InvoicesSettingsProps> = ({ id, values, onChange, onValidationChange }) => {
    const t = useTranslations();
    const features = useSelector(getFeatures);
    const generalSettings = useGeneralSettings();
    const hasInvoicesFeature = useMemo(() => features.includes(FeatureEnum.Invoices), [features]);
    const isFrenchCountry = (generalSettings?.countryCode ?? '').toLowerCase().startsWith('fr');
    const branchEntries = useMemo(
        () => Object.entries(generalSettings?.branches ?? {}),
        [generalSettings?.branches]
    );
    const isMultiBranchTenant = branchEntries.length > 1;
    const [activeBranchTab, setActiveBranchTab] = useState<string | null>(
        () => branchEntries.length > 0 ? branchEntries[0][0] : null
    );
    const [activeMainTab, setActiveMainTab] = useState(0);
    const {
        taxRates,
        taxExemptions,
        taxOptions,
        taxExemptionOptionsMap,
        taxExemptionOptions,
        getTaxDisplayName,
        getTaxIdFromDisplayName,
        isExemptTax,
        getDefaultExemption,
    } = useTaxData();

    const isSimulationMode = values.provider === InvoicesProvider.Simulated;
    const appointmintInvoices: IntegrationAppointmintInvoices = values?.appointmintInvoices ?? {
        autoridadeTributariaCreds: {},
    };
    const atCreds = appointmintInvoices.autoridadeTributariaCreds ?? {};
    const companyLegalInfo = values.companyLegalInfo;

    if (!hasInvoicesFeature) {
        return (
            <Typography id={`${id}-invoices-unavailable`} size='small' block>
                {t(TK.billingModuleNotActivatedMessage)}
            </Typography>
        );
    }

    if (isFrenchCountry) {
        return (
            <Alert color="warning" id={`${id}-invoices-country-unsupported`}>
                {t(TK.invoicesNotSupportedInThisCountry)}
            </Alert>
        );
    }

    const lang = getLanguage();
    const certifiedText = lang === 'pt' ? 'Software Certificado n.º 2230/AT' : 'Certified Software No. 2230/AT.';

    // SAFT validation
    const saftEmailsEmpty = !isSimulationMode && values.sendSaftReportAutomaticallyToAt && !values.saftReportRecipientEmails?.trim();
    const saftDayEmpty = !isSimulationMode && values.sendSaftReportAutomaticallyToAt && !values.saftReportSendDay;
    const saftDayInvalid = !isSimulationMode && values.sendSaftReportAutomaticallyToAt && !!values.saftReportSendDay && (values.saftReportSendDay < 1 || values.saftReportSendDay > 28);
    const hasSaftValidationErrors = saftEmailsEmpty || saftDayEmpty || saftDayInvalid;

    // AT section validation — only applies outside simulation mode and once the user starts filling AT fields
    const hasAnyAtFieldFilled = !isSimulationMode && !!(
        atCreds.fiscalId?.trim() ||
        atCreds.atUser?.trim() ||
        atCreds.atPassword?.trim()
    );
    const fiscalIdEmpty = hasAnyAtFieldFilled && !atCreds.fiscalId?.trim();
    const atUserEmpty = hasAnyAtFieldFilled && !atCreds.atUser?.trim();
    const atPasswordEmpty = hasAnyAtFieldFilled && !atCreds.atPassword?.trim();

    const isAddressIncomplete = (info?: CompanyLegalInfo) =>
        !info?.address?.trim() || !info?.city?.trim() || !info?.postalCode?.trim();

    const hasAtAddressErrors = hasAnyAtFieldFilled && (
        isMultiBranchTenant
            ? branchEntries.some(([branchId]) => isAddressIncomplete(values.branchesLegalInfo?.[branchId]))
            : isAddressIncomplete(companyLegalInfo)
    );
    const hasAtValidationErrors = fiscalIdEmpty || atUserEmpty || atPasswordEmpty || hasAtAddressErrors;
    const hasAnyValidationErrors = hasSaftValidationErrors || hasAtValidationErrors;

    // AT integration is considered configured once the backend has activated it.
    const isAtConfigured = !!values.activatedAt;

    React.useEffect(() => {
        onValidationChange?.(!hasAnyValidationErrors);
    }, [hasAnyValidationErrors, onValidationChange]);

    // Set default tax rate to 23% when taxId is empty
    React.useEffect(() => {
        if (taxRates.length === 0) return;
        const defaultTaxRate = taxRates.find(rate => rate.rate === 23) ?? taxRates[0];
        if (!defaultTaxRate) return;

        if (isMultiBranchTenant) {
            const missingBranches = branchEntries.filter(([branchId]) => !values.branchesLegalInfo?.[branchId]?.taxId);
            if (missingBranches.length > 0) {
                const updatedBranchesLegalInfo = { ...(values.branchesLegalInfo ?? {}) };
                missingBranches.forEach(([branchId]) => {
                    updatedBranchesLegalInfo[branchId] = {
                        ...(updatedBranchesLegalInfo[branchId] ?? {}),
                        taxId: defaultTaxRate.id,
                    };
                });
                onChange({ ...values, branchesLegalInfo: updatedBranchesLegalInfo });
            }
        } else if (!companyLegalInfo?.taxId) {
            onChange({ ...values, companyLegalInfo: { ...companyLegalInfo, taxId: defaultTaxRate.id } });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyLegalInfo?.taxId, taxRates, isMultiBranchTenant]);

    // Set default SAFT send day to 2 when checkbox is enabled and day is not set
    React.useEffect(() => {
        if (values.sendSaftReportAutomaticallyToAt && !values.saftReportSendDay) {
            onChange({ ...values, saftReportSendDay: 2 });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.sendSaftReportAutomaticallyToAt, values.saftReportSendDay]);

    const renderTaxFields = (legalInfo: CompanyLegalInfo, onLegalInfoChange: (updated: CompanyLegalInfo) => void) => (
        <Row>
            <Col md={4}>
                <ComboField
                    id={`${id}-company-legal-tax-id`}
                    label={t(TK.taxId)}
                    helpText={t(TK.invoiceDefaultTaxInfo)}
                    value={getTaxDisplayName(legalInfo?.taxId)}
                    options={taxOptions}
                    onChange={displayValue => {
                        const taxId = getTaxIdFromDisplayName(displayValue);
                        const isExempt = isExemptTax(taxId);
                        const shouldSetDefaultExemption = isExempt && !legalInfo.taxExemption;
                        const defaultExemption = getDefaultExemption();
                        onLegalInfoChange({
                            ...legalInfo,
                            taxId,
                            taxExemption: isExempt ? (shouldSetDefaultExemption ? defaultExemption.id : legalInfo.taxExemption) : undefined,
                            taxExemptionLaw: isExempt ? (shouldSetDefaultExemption ? defaultExemption.law : legalInfo.taxExemptionLaw) : undefined,
                        });
                    }}
                />
            </Col>
            {isExemptTax(legalInfo?.taxId) && (
                <>
                    <Col md={4}>
                        <ComboField
                            id={`${id}-company-legal-tax-exemption`}
                            label={t(TK.taxExemption)}
                            value={legalInfo.taxExemption ? (taxExemptionOptionsMap[legalInfo.taxExemption] || legalInfo.taxExemption) : (taxExemptions.length > 0 ? taxExemptionOptionsMap[taxExemptions[0].id] : '')}
                            options={taxExemptionOptions}
                            onChange={displayValue => {
                                const taxExemption = displayValue?.substring(0, 3) || (taxExemptions.length > 0 ? taxExemptions[0].id : '');
                                const exemptionObj = taxExemptions.find(e => e.id === taxExemption);
                                onLegalInfoChange({ ...legalInfo, taxExemption, taxExemptionLaw: exemptionObj?.law?.[0] || undefined });
                            }}
                        />
                    </Col>
                    <Col md={4}>
                        <TextInputField
                            id={`${id}-company-legal-tax-exemption-law`}
                            label={t(TK.taxExemptionLaw)}
                            value={legalInfo?.taxExemptionLaw || ''}
                            type="text"
                            onChange={taxExemptionLaw => onLegalInfoChange({ ...legalInfo, taxExemptionLaw })}
                        />
                    </Col>
                </>
            )}
        </Row>
    );

    const renderAddressFields = (
        branchId: string | null,
        legalInfo: CompanyLegalInfo,
        onLegalInfoChange: (updated: CompanyLegalInfo) => void
    ) => {
        const suffix = branchId ? `-${branchId}` : '';
        const addressEmpty = hasAnyAtFieldFilled && !legalInfo?.address?.trim();
        const cityEmpty = hasAnyAtFieldFilled && !legalInfo?.city?.trim();
        const postalCodeEmpty = hasAnyAtFieldFilled && !legalInfo?.postalCode?.trim();

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)' }}>
                <Row>
                    <Col md={12}>
                        <TextInputField
                            id={`${id}-company-legal-address${suffix}`}
                            label={t(TK.address)}
                            value={legalInfo?.address}
                            type="text"
                            required={hasAnyAtFieldFilled}
                            invalid={addressEmpty}
                            disabled={isSimulationMode}
                            onChange={address => onLegalInfoChange({ ...legalInfo, address })}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <TextInputField
                            id={`${id}-company-legal-city${suffix}`}
                            label={t(TK.city)}
                            value={legalInfo?.city}
                            type="text"
                            required={hasAnyAtFieldFilled}
                            invalid={cityEmpty}
                            disabled={isSimulationMode}
                            onChange={city => onLegalInfoChange({ ...legalInfo, city })}
                        />
                    </Col>
                    <Col md={6}>
                        <TextInputField
                            id={`${id}-company-legal-postal-code${suffix}`}
                            label={t(TK.postalCode)}
                            value={legalInfo?.postalCode}
                            type="text"
                            required={hasAnyAtFieldFilled}
                            invalid={postalCodeEmpty}
                            disabled={isSimulationMode}
                            onChange={postalCode => onLegalInfoChange({ ...legalInfo, postalCode })}
                        />
                    </Col>
                </Row>
            </div>
        );
    };

    return (
        <Form>
            <Alert color="secondary" isOpen>
                {t(TK.invoicesSettingsDescription)}
            </Alert>

            <Row>
                <Col md={12}>
                    <SwitchField
                        id={`${id}-activate-simulate-mode`}
                        label={t(TK.activateSimulateMode)}
                        helpText={t(TK.simulateModeTooltip)}
                        value={isSimulationMode}
                        onChange={(enabled) => onChange({
                            ...values,
                            provider: enabled ? InvoicesProvider.Simulated : InvoicesProvider.Appointmint,
                        })}
                    />
                </Col>
            </Row>

            <AppTabs
                value={isSimulationMode ? 0 : activeMainTab}
                onChange={(_, v) => setActiveMainTab(v)}
                indicatorColor="primary"
                textColor="primary"
                style={{ marginTop: 'var(--spacer-S)', marginBottom: 'var(--spacer-S)', borderBottom: 'var(--border-thin) solid var(--appLigthGrey)' }}
            >
                <Tab label={t(TK.invoicesSettingsTabLabel)} />
                {!isSimulationMode && <Tab label={t(TK.atSetupSectionTitle)} />}
            </AppTabs>

            {(isSimulationMode || activeMainTab === 0) && (
            <>
            {isSimulationMode && (
                <Row style={{ marginTop: 'var(--spacer-S)' }}>
                    <Col md={12}>
                        <Alert color='warning' id={`${id}-simulation-mode-alert`}>
                            {t(TK.simulationModeActiveNotice)}
                        </Alert>
                    </Col>
                </Row>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', marginTop: 'var(--spacer-S)' }}>
                <CheckInputField
                    id={`${id}-send-invoices-email`}
                    label={t(TK.sendInvoicesAutomaticallyToClientsByEmail)}
                    value={!!values.sendInvoicesAutomaticallyToClientsByEmail}
                    onChange={(v) => onChange({ ...values, sendInvoicesAutomaticallyToClientsByEmail: v })}
                />
                <CheckInputField
                    id={`${id}-emit-on-payment`}
                    label={t(TK.emitInvoiceAutomaticallyOnPaymentConfirmation)}
                    disabled
                    value={!!values.emitInvoiceAutomaticallyOnPaymentConfirmation}
                    onChange={(v) => onChange({ ...values, emitInvoiceAutomaticallyOnPaymentConfirmation: v })}
                />
                <CheckInputField
                    id={`${id}-add-appointment-pack-details`}
                    label={t(TK.addAppointmentPackDetailsAutomaticallyOnInvoice)}
                    value={!!values.addAppointmentPackDetailsAutomaticallyOnInvoice}
                    onChange={(v) => onChange({ ...values, addAppointmentPackDetailsAutomaticallyOnInvoice: v })}
                />
                {values.addAppointmentPackDetailsAutomaticallyOnInvoice && (
                    <div style={{ paddingLeft: 'var(--spacer-M)' }}>
                        <PlaceholderTextField
                            id={`${id}-appointment-pack-details-template`}
                            label={t(TK.appointmentPackDetailsTemplate)}
                            helpText={t(TK.appointmentPackDetailsTemplatePlaceholdersNote)}
                            value={values.appointmentPackDetailsTemplate || DEFAULT_APPOINTMENT_PACK_DETAILS_TEMPLATE}
                            type="text"
                            onChange={raw => {
                                const trimmed = raw.trim();
                                onChange({ ...values, appointmentPackDetailsTemplate: trimmed || DEFAULT_APPOINTMENT_PACK_DETAILS_TEMPLATE });
                            }}
                        />
                    </div>
                )}
                <CheckInputField
                    id={`${id}-send-saft-at`}
                    label={t(TK.sendSaftReportAutomaticallyToAT)}
                    helpText={t(TK.sendSaftReportAutomaticallyToATHelp)}
                    value={!!values.sendSaftReportAutomaticallyToAt}
                    onChange={(v) => onChange({ ...values, sendSaftReportAutomaticallyToAt: v })}
                />
                {values.sendSaftReportAutomaticallyToAt && (
                    <Row style={{ paddingLeft: 'var(--spacer-M)', margin: 0 }}>
                        <Col md={9}>
                            <TextInputField
                                id={`${id}-saft-emails`}
                                label={t(TK.recipientEmails)}
                                value={values.saftReportRecipientEmails || ''}
                                type="text"
                                placeholder="email1@example.com;email2@example.com"
                                required
                                invalid={saftEmailsEmpty}
                                onChange={saftReportRecipientEmails => onChange({ ...values, saftReportRecipientEmails })}
                            />
                        </Col>
                        <Col md={3}>
                            <TextInputField
                                id={`${id}-saft-day`}
                                label={t(TK.sendDay)}
                                value={values.saftReportSendDay ?? ''}
                                type="number"
                                min={1}
                                max={28}
                                required
                                invalid={saftDayEmpty || saftDayInvalid}
                                onChange={day => {
                                    const dayNum = day ? parseInt(day, 10) : undefined;
                                    onChange({ ...values, saftReportSendDay: dayNum });
                                }}
                            />
                        </Col>
                    </Row>
                )}
                <AutocompleteTextField
                    id={`${id}-default-payment-method`}
                    label={t(TK.defaultInvoicePaymentMethod)}
                    helpText={t(TK.defaultInvoicePaymentMethodHelp)}
                    value={values.defaultPaymentMethod ?? ''}
                    options={generalSettings?.paymentMethods ?? []}
                    onValueChanged={defaultPaymentMethod => onChange({ ...values, defaultPaymentMethod: defaultPaymentMethod || undefined })}
                />
                <Row>
                    <Col md={12}>
                        <PlaceholderTextField
                            id={`${id}-general-observations`}
                            label={t(TK.generalInvoiceObservations)}
                            value={values.generalObservations || ''}
                            type="textarea"
                            onChange={generalObservations => onChange({ ...values, generalObservations })}
                        />
                    </Col>
                </Row>
                {renderTaxFields(
                    companyLegalInfo,
                    (updated) => onChange({ ...values, companyLegalInfo: updated })
                )}
            </div>

            <SaveRow>
                <Typography size='smaller'>
                    {values.activatedAt
                        ? `${t(TK.activatedAtLabel)} ${moment(values.activatedAt).format('LL')}`
                        : t(TK.notActivatedYet)}
                </Typography>
                <AppButton
                    id={`${id}-save-button`}
                    color="success"
                    disabled={hasSaftValidationErrors}
                    onClick={() => onChange(values, true)}
                >
                    {t(TK.save)}
                </AppButton>
            </SaveRow>
            </>
            )}

            {!isSimulationMode && activeMainTab === 1 && (
            <>
            <AtSectionHeader style={{ marginTop: 'var(--spacer-M)' }}>
                <Typography size='medium'><b>{t(TK.atSetupSectionTitle)}</b></Typography>
                <StatusBadge $configured={isAtConfigured}>
                    {isAtConfigured ? t(TK.atIntegrationConfigured) : t(TK.atIntegrationNotConfigured)}
                </StatusBadge>
            </AtSectionHeader>

            <SectionRow>
                <Row style={{ justifyContent: 'center', marginBottom: 0 }}>
                    <Col sm={12}>
                        <AtLogoCol>
                            <img src='/at-logo.png' alt='AT logo' />
                            <Typography size='smaller' block style={{ marginBottom: 'var(--spacer-S)' }}><b>{certifiedText}</b></Typography>
                        </AtLogoCol>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <TextInputField
                            id={`${id}-at-cred-fiscal-id`}
                            label={t(TK.atFiscalId)}
                            value={atCreds.fiscalId}
                            type="text"
                            required={hasAnyAtFieldFilled}
                            invalid={fiscalIdEmpty}
                            disabled={isSimulationMode}
                            onChange={fiscalId => onChange({
                                ...values,
                                appointmintInvoices: {
                                    ...appointmintInvoices,
                                    autoridadeTributariaCreds: { ...atCreds, fiscalId },
                                },
                            })}
                        />
                    </Col>
                    <Col md={4}>
                        <TextInputField
                            id={`${id}-at-cred-user`}
                            label={t(TK.atUsername)}
                            helpText={t(TK.atUsernameHelp)}
                            value={atCreds.atUser}
                            type="number"
                            required={hasAnyAtFieldFilled}
                            invalid={atUserEmpty}
                            disabled={isSimulationMode}
                            onChange={atUser => onChange({
                                ...values,
                                appointmintInvoices: {
                                    ...appointmintInvoices,
                                    autoridadeTributariaCreds: { ...atCreds, atUser },
                                },
                            })}
                        />
                    </Col>
                    <Col md={4}>
                        <TextInputField
                            id={`${id}-at-cred-password`}
                            label={t(TK.password)}
                            value={atCreds.atPassword ?? ''}
                            placeholder={atCreds.atUser ? '••••••••' : ''}
                            type="password"
                            required={hasAnyAtFieldFilled}
                            invalid={atPasswordEmpty}
                            disabled={isSimulationMode}
                            onChange={atPassword => onChange({
                                ...values,
                                appointmintInvoices: {
                                    ...appointmintInvoices,
                                    autoridadeTributariaCreds: { ...atCreds, atPassword: atPassword || undefined },
                                },
                            })}
                        />
                    </Col>
                </Row>

                {isMultiBranchTenant ? (
                    <>
                        <Nav tabs>
                            {branchEntries.map(([branchId, branch]) => (
                                <TabButton
                                    key={branchId}
                                    active={activeBranchTab === branchId}
                                    setActive={() => setActiveBranchTab(branchId)}
                                >
                                    {branch.displayName || branchId}
                                </TabButton>
                            ))}
                        </Nav>
                        {branchEntries.map(([branchId]) => {
                            if (activeBranchTab !== branchId) return null;
                            const branchLegalInfo: CompanyLegalInfo = values.branchesLegalInfo?.[branchId] ?? {};
                            return (
                                <div key={branchId} style={{ marginTop: 'var(--spacer-S)' }}>
                                    {renderAddressFields(
                                        branchId,
                                        branchLegalInfo,
                                        (updated) => onChange({
                                            ...values,
                                            branchesLegalInfo: { ...(values.branchesLegalInfo ?? {}), [branchId]: updated },
                                        })
                                    )}
                                </div>
                            );
                        })}
                    </>
                ) : (
                    renderAddressFields(
                        null,
                        companyLegalInfo,
                        (updated) => onChange({ ...values, companyLegalInfo: updated })
                    )
                )}
            </SectionRow>

            <SaveRowRight>
                <AppButton
                    id={`${id}-save-credentials-button`}
                    color="success"
                    disabled={isSimulationMode || hasAtValidationErrors}
                    onClick={() => onChange(values, true)}
                >
                    {t(TK.saveCredentials)}
                </AppButton>
            </SaveRowRight>
            </>
            )}
        </Form>
    );
}

export default InvoicesSettings;
